import { useState } from "react";
import { cn } from "~/lib/utils";
import { Icon } from "../icons/icon";
import { AddFieldButton } from "./AddFieldButton";
import { InlineTextareaControl } from "./InlineTextareaControl";

interface EditableTextareaProps {
  value: string | null | undefined;
  /** Called when user commits a change. Resolve = exit edit mode, reject = stay in edit mode. */
  onSave: (value: string | null) => Promise<void>;
  /** Called when user modifies input (for clearing errors, live validation, etc.) */
  onChange?: (value: string | null) => void;
  placeholder?: string;
  addLabel: string;
  canEdit?: boolean;
  /** Error message to display while editing. Controlled by parent. */
  error?: string;
  /** Loading state while saving. Controlled by parent. */
  saving?: boolean;
}

export function EditableTextarea({
  value,
  onSave,
  onChange,
  placeholder,
  addLabel,
  canEdit,
  error,
  saving = false,
}: EditableTextareaProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const icon = <Icon i="text" className="size-4" />;

  const startEditing = () => {
    setEditing(true);
    setEditValue(value ?? "");
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditValue("");
  };

  const handleChange = (newValue: string) => {
    setEditValue(newValue);
    onChange?.(newValue.trim() || null);
  };

  const save = async () => {
    const trimmed = editValue.trim();

    if (trimmed === (value ?? "")) {
      cancelEditing();
      return;
    }

    try {
      await onSave(trimmed || null);
      cancelEditing();
    } catch {
      // Stay in edit mode - parent controls error display via prop
    }
  };

  if (editing) {
    return (
      <InlineTextareaControl
        value={editValue}
        onChange={handleChange}
        onSave={save}
        onCancel={cancelEditing}
        placeholder={placeholder}
        disabled={saving}
        error={error}
        icon={icon}
      />
    );
  }

  if (value) {
    return (
      <div
        className={cn("flex gap-2", { "cursor-pointer group": canEdit })}
        onClick={canEdit ? startEditing : undefined}
      >
        {canEdit && (
          <Icon
            i="text"
            className="size-4 text-muted-foreground shrink-0 mt-0.5 group-hover:text-primary transition-colors"
          />
        )}
        <p
          className={cn("text-sm text-foreground whitespace-pre-wrap", {
            "group-hover:text-primary transition-colors": canEdit,
          })}
        >
          {value}
        </p>
      </div>
    );
  }

  if (canEdit) {
    return <AddFieldButton icon={icon} label={addLabel} onClick={startEditing} />;
  }

  return null;
}
